import { Box, CircularProgress, Container } from "@mui/material";
import DashboardCardHeader from "./DashboardCardHeader";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { RopChart } from "../../../main/components/RopChart";
import {
  AggregatedInversor,
  BaseErrorCard,
} from "../models/dados-indicadores.model";
import { EChartsOption } from "echarts";
import { DashboardContext } from "../context/DashboardContexProvider";
import { EmptyTable } from "../../clients/components/EmptyState";
import { CardTypesEnum } from "../types";
import { getCardIsLoading } from "./TextCardContent";
import { MainContext } from "../../../main/context/MainContextProvider";
import { usinasApi } from "../../../services/api";
import type { Usina } from "../../usinas/types";
import {
  getHeatMapChartDataByAggregatedInversorArray,
  heatmapHasRenderableData,
  mergeAggregatedInversores,
} from "../helpers/mergeHeatmapInversoresWithUsinaConfig";
import type { SunburstChartProps, PlantaUGAtMinutes } from "./SunBurstChart";
import type { CardInfo } from "../types";

function useStablePlantaMinutesUGs(
  cardsConfig: CardInfo[],
): PlantaUGAtMinutes[] | undefined {
  const cacheRef = useRef<{
    key: string;
    value: PlantaUGAtMinutes[] | undefined;
  }>({ key: "", value: undefined });

  const sunburstCard = cardsConfig.find(
    (card) => card.type === CardTypesEnum.GeracaoPlanta,
  );
  const uGs = sunburstCard
    ? (sunburstCard.childrenProps as SunburstChartProps).data?.plantaMinutes?.uGs
    : undefined;

  const key = uGs
    ? `${uGs.length}:${uGs[0]?.name ?? ""}:${uGs[0]?.lastPotenciaAtivaMinute ?? 0}:${uGs[uGs.length - 1]?.name ?? ""}:${uGs.reduce((sum, ug) => sum + (ug.inversores?.length ?? 0), 0)}`
    : "";

  if (key !== cacheRef.current.key) {
    cacheRef.current = { key, value: uGs };
  }

  return cacheRef.current.value;
}

function getInversoresMemoKey(
  inversores: AggregatedInversor[] | undefined,
  lastUpdate: Date,
): string {
  const list = inversores ?? [];
  const firstSeriesLength = list[0]?.potenciaAtivaAtMinutes?.length ?? 0;
  const lastSeriesLength =
    list[list.length - 1]?.potenciaAtivaAtMinutes?.length ?? 0;
  const lastName = list[list.length - 1]?.name ?? "";
  return `${lastUpdate.getTime()}:${list.length}:${firstSeriesLength}:${lastSeriesLength}:${lastName}`;
}

export type HeatmapChartProps = {
  cardId?: string;
  title?: string;
  lastUpdate: Date;
  cardType: number;
  data: {
    inversores?: AggregatedInversor[];
    cardError: BaseErrorCard;
  };
};

export default function HeatmapChart({
  lastUpdate,
  cardId,
  data,
  title,
  cardType,
}: HeatmapChartProps) {
  const {
    cardsDisplayableColorProps,
    setCardsDisplayableColorProps,
    cardIsLoading,
    requestParamCards,
    cardsConfig,
  } = useContext(DashboardContext);
  const { selectedDashboardUsina } = useContext(MainContext);

  const chartHeatmap = useRef(null);

  const [isMouseIn, setIsMouseIn] = useState<boolean>(false);
  const [currentUsina, setCurrentUsina] = useState<Usina>();

  useEffect(() => {
    let isMounted = true;

    const loadUsinaConfig = async () => {
      if (!selectedDashboardUsina?.usinaIds.id) {
        setCurrentUsina(undefined);
        return;
      }

      try {
        const usinaResponse = await usinasApi.getUsina(
          selectedDashboardUsina.usinaIds.id,
        );
        if (isMounted) {
          setCurrentUsina(usinaResponse);
        }
      } catch (error) {
        console.error(
          "Erro ao carregar configuração da usina para HeatmapChart",
          error,
        );
        if (isMounted) {
          setCurrentUsina(undefined);
        }
      }
    };

    loadUsinaConfig();

    return () => {
      isMounted = false;
    };
  }, [selectedDashboardUsina?.usinaIds.id]);

  const plantaMinutesUGs = useStablePlantaMinutesUGs(cardsConfig);

  const inversoresKey = useMemo(
    () => getInversoresMemoKey(data.inversores, lastUpdate),
    [data.inversores, lastUpdate],
  );

  const configuredUGsKey = useMemo(
    () =>
      `${currentUsina?.uGs?.length ?? 0}:${currentUsina?.uGs?.[0]?.name ?? ""}:${currentUsina?.uGs?.[currentUsina.uGs.length - 1]?.name ?? ""}`,
    [currentUsina?.uGs],
  );

  const mergedInversores = useMemo(
    () =>
      mergeAggregatedInversores(
        currentUsina?.uGs,
        data.inversores,
        plantaMinutesUGs,
      ),
    [configuredUGsKey, inversoresKey, plantaMinutesUGs, currentUsina?.uGs, data.inversores],
  );

  const chartSeries = useMemo(
    () => getHeatMapChartDataByAggregatedInversorArray(mergedInversores),
    [mergedInversores],
  );

  const { chartData, max, minutes, inversoresNames } = chartSeries;

  const [maxColor, setMaxColor] = useState<string>(
    cardsDisplayableColorProps.heatmap.maxColor
  );
  const [minColor, setMinColor] = useState<string>(
    cardsDisplayableColorProps.heatmap.minColor
  );

  const option: EChartsOption = useMemo(
    () => ({
      backgroundColor: "transparent",
      animation: false,
      tooltip: {
        show: true,
        formatter: function (params: any) {
          return (
            "Inversor: " +
            params.value[1] +
            "<br>" +
            "Hora: " +
            params.value[0] +
            "<br>" +
            "Potência Ativa: " +
            params.value[2].toFixed(2) +
            " MW"
          );
        },
      },
      grid: {
        height: "75%",
        top: "5%",
        bottom: "10%",
      },
      xAxis: {
        type: "category",
        data: minutes,
        axisLabel: {
          show: true,
          fontSize: "10px",
          interval: (index: number, value: string) => {
            // Label on the hour (:00) or every 3rd 5-min bucket
            return value.endsWith(":00") || index % 3 === 0;
          },
        },
        splitArea: {
          show: false,
        },
      },
      yAxis: {
        type: "category",
        data: inversoresNames,
        axisLabel: {
          show: true,
          fontSize: "10px",
        },
      },
      visualMap: {
        show: true,
        min: 0,
        max: max > 0 ? max : 1,
        calculable: true,
        textStyle: {
          fontSize: "10px",
        },
        left: "center",
        orient: "horizontal",
        bottom: "5%",
        inRange: {
          color: [minColor, maxColor],
        },
        formatter: function (value) {
          const valueNumber = Number(value);
          return valueNumber.toFixed(2) + " MW";
        },
      },
      series: [
        {
          name: "",
          type: "heatmap",
          data: chartData,
          progressive: 400,
          progressiveThreshold: 1500,
          animation: false,
          label: {
            show: false,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    }),
    [chartData, max, minutes, inversoresNames, minColor, maxColor],
  );

  const hasDelay = useMemo(() => {
    const delay = lastUpdate
      ? (Date.now() - lastUpdate.getTime()) / 1000
      : 0;
    return delay > 120;
  }, [lastUpdate]);

  const headerTitle = title ?? "Heat Map - Potência Ativa por Inversor";

  const [openModal, setopenModal] = useState<boolean>(false);

  const handleConfirmModal = () => {
    setCardsDisplayableColorProps({
      ...cardsDisplayableColorProps,
      heatmap: {
        maxColor: maxColor,
        minColor: minColor,
      },
    });
    setopenModal(false);
  };

  const hasData = heatmapHasRenderableData(mergedInversores);

  return (
    <>
      <Box
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseOver={() => setIsMouseIn(true)}
        onMouseLeave={() => setIsMouseIn(false)}
      >
        <DashboardCardHeader
          cardError={data.cardError}
          hasDelay={hasDelay}
          lastUpdate={lastUpdate}
          cardId={cardId}
          elementRef={chartHeatmap}
          isMouseIn={isMouseIn}
          title={headerTitle}
          titleFontSize={21}
          openColorsModal={openModal}
          setOpenColorsModal={setopenModal}
          onConfirmColorsModal={handleConfirmModal}
          colorButton
          colorsOptions={[
            {
              title: "Cor do valor mínimo",
              value: minColor,
              setValue: setMinColor,
            },
            {
              title: "Cor do valor mázimo",
              value: maxColor,
              setValue: setMaxColor,
            },
          ]}
          cardType={CardTypesEnum.Heatmap}
          hasFilter
        />
        {!getCardIsLoading(cardType, cardIsLoading, requestParamCards) ? (
          hasData ? (
            <Container
              ref={chartHeatmap}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "90%",
              }}
            >
              <RopChart option={option} />
            </Container>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyTable title="Nenhum dado encontrado!" />
            </Box>
          )
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </>
  );
}
